import { useEffect, useState } from "react";
import { Loader2, Boxes, AlertTriangle, CalendarClock, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, StatCard } from "@/components/common/ui";
import { cn } from "@/lib/utils";

const S = { ok: "#12B76A", "low-stock": "#F04438", expiring: "#F79009", expired: "#F04438", reordered: "#155EEF" };

export default function Assets() {
  const [assets, setAssets] = useState(null);
  const [pos, setPos] = useState([]);
  const [cat, setCat] = useState("");
  const load = () => api.get("/assets").then((r) => setAssets(r.data)).catch(() => setAssets([]));
  const loadPo = () => api.get("/assets/purchase-orders").then((r) => setPos(r.data)).catch(() => {});
  useEffect(() => { load(); loadPo(); }, []);

  const reorder = async (id) => { try { const { data } = await api.post(`/assets/${id}/reorder`); toast.success(data.message || "Reordered"); load(); loadPo(); } catch (e) { toast.error("Failed"); } };
  if (!assets) return <div className="flex h-64 items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>;

  const cats = [...new Set(assets.map((a) => a.category))];
  const items = cat ? assets.filter((a) => a.category === cat) : assets;
  const low = assets.filter((a) => a.status === "low-stock").length;
  const exp = assets.filter((a) => ["expiring", "expired"].includes(a.status)).length;

  return (
    <div>
      <PageHeader overline="Operations · Module 4 · Asset Optimization" title="Assets & Resource Optimization"
        description="Inventory, expiry and equipment lifecycle with AI reorder and expiry prediction to prevent shortages and reduce waste." />
      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard icon={Boxes} label="Tracked Items" value={assets.length} accent="#155EEF" index={0} />
        <StatCard icon={AlertTriangle} label="Low Stock" value={low} accent="#F04438" index={1} onClick={() => setCat("")} />
        <StatCard icon={CalendarClock} label="Expiring / Expired" value={exp} accent="#F79009" index={2} />
        <StatCard icon={ShoppingCart} label="Purchase Orders" value={pos.length} accent="#7C3AED" index={3} />
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        <button onClick={() => setCat("")} className={cn("rounded-full border px-3 py-1 text-xs font-medium", !cat ? "border-[#155EEF] bg-[#155EEF] text-white" : "bg-card")}>All</button>
        {cats.map((c) => <button key={c} onClick={() => setCat(c)} className={cn("rounded-full border px-3 py-1 text-xs font-medium", cat === c ? "border-[#155EEF] bg-[#155EEF] text-white" : "bg-card")}>{c}</button>)}
      </div>

      <div className="overflow-hidden rounded-xl border bg-card shadow-sm mb-8">
        <div className="careiq-scroll overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="px-4 py-3">Item</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Qty</th><th className="px-4 py-3">Reorder @</th><th className="px-4 py-3">Location</th><th className="px-4 py-3">Expiry</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Action</th></tr>
            </thead>
            <tbody className="divide-y">
              {items.map((a) => (
                <tr key={a.id} data-testid={`asset-${a.id}`} className="hover:bg-muted/40">
                  <td className="px-4 py-3 font-medium">{a.name}</td><td className="px-4 py-3 text-muted-foreground">{a.category}</td>
                  <td className="px-4 py-3 font-semibold" style={{ color: a.quantity <= a.reorder_point * 0.4 ? "#F04438" : "inherit" }}>{a.quantity}</td>
                  <td className="px-4 py-3 text-muted-foreground">{a.reorder_point}</td><td className="px-4 py-3 text-muted-foreground text-xs">{a.location}</td>
                  <td className="px-4 py-3 text-muted-foreground">{a.expiry || "—"}</td>
                  <td className="px-4 py-3"><span className="rounded-full px-2 py-0.5 text-xs font-semibold capitalize" style={{ color: S[a.status], backgroundColor: S[a.status] + "1a" }}>{a.status.replace("-", " ")}</span></td>
                  <td className="px-4 py-3 text-right">
                    {["low-stock", "expiring", "expired"].includes(a.status) ? (
                      <button data-testid={`reorder-${a.id}`} onClick={() => reorder(a.id)} className="rounded-md bg-[#155EEF] px-2.5 py-1 text-xs font-semibold text-white hover:bg-[#0F4FD1]">Reorder</button>
                    ) : a.status === "reordered" ? <span className="text-xs font-medium text-[#155EEF]">PO created</span> : <span className="text-xs text-muted-foreground">OK</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <h2 className="mb-3 text-lg font-bold tracking-tight">Purchase Orders</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {pos.map((p) => (
          <div key={p.id} className="rounded-xl border bg-card p-4 shadow-sm">
            <div className="flex items-center justify-between"><span className="font-semibold">{p.vendor}</span>
              <span className="rounded-full px-2 py-0.5 text-xs font-semibold capitalize" style={{ color: p.status === "pending" ? "#F79009" : p.status === "received" ? "#12B76A" : "#155EEF", backgroundColor: (p.status === "pending" ? "#F79009" : p.status === "received" ? "#12B76A" : "#155EEF") + "1a" }}>{p.status}</span></div>
            <div className="mt-1 text-xs text-muted-foreground">{p.asset_name || `${p.items} item(s)`}</div>
            <div className="mt-2 text-lg font-bold">${p.total.toLocaleString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
